/* eslint-disable max-lines */
import { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getPlayerHeight } from 'app/store/AudioPlayer/selectors';
import { ReactComponent as MenuIcon } from 'resources/images/menu.svg';
import { ReactComponent as AddIcon } from 'resources/images/add.svg';
import { routes } from 'app/routing/routesConfig';
import { Flex } from '@chakra-ui/layout';
import { Box, Card, IconButton, Text, VStack } from '@chakra-ui/react';
import {
  useResizableBlock,
  getMaxWidth,
  getMinWidth,
  SIDEBAR_KEY,
} from 'app/utils/hooks/useResizableBlock';
import { createFolderEffect } from 'app/store/SongList/effects';
import { getSongsFolders } from 'app/store/SongList/selectors';
import { ContextMenu, useContextMenu } from 'app/components/ChakraTableList';
import { FolderItem } from 'types/song';
import debounce from 'lodash/debounce';
import InfiniteScroll from 'react-infinite-scroll-component';
import { DndProvider } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import { SongFolderRenameDialog } from './SongFolderRenameDialog';
import { SongFolderRemoveDialog } from './SongFolderRemoveDialog';
import { getNextFolderTitle } from './utils';
import { SiderBarLink } from './SiderBarLink';
import { SongFolder } from './SongFolder';
import { getStyles } from './styles';
import { useSongFoldersInfiniteLoader } from './useSongFoldersInfiniteLoader';
import { HEADER_HEIGHT } from '../HeaderBar';
import { mainMenu } from './constants';

export const SideBar = () => {
  const dispatch = useAppDispatch();
  const songsFolders = useAppSelector(getSongsFolders);
  const [renameSongFolder, setRenameSongFolder] = useState<FolderItem | null>(
    null
  );

  const [removeSongFolder, setRemoveSongFolder] = useState<FolderItem | null>(
    null
  );

  const {
    panelRef,
    cursor,
    showFullHeader,
    handleMouseDown,
    resize,
    toggleMinMax,
    isDragging,
    forceCompactMode,
    isCompact,
  } = useResizableBlock();
  const playerHeight = useAppSelector(getPlayerHeight);
  const height = `calc(100vh - ${HEADER_HEIGHT}px - ${playerHeight}px)`;
  const contextMenuProps = useContextMenu();
  const { setOptions, setPosition } = contextMenuProps;

  const songFolders = useMemo(
    () =>
      songsFolders.map(({ guid, title, songCount = 0 }) => ({
        guid,
        to: routes.songFolder.path.replace(':guid', guid),
        label: title,
        subLabel: `${songCount} Song${songCount === 1 ? '' : 's'}`,
        setOptions,
        setPosition,
        setRenameSongFolder,
        setRemoveSongFolder,
      })),
    [setOptions, setPosition, songsFolders]
  );

  const {
    rootSx,
    wrapperSx,
    contentSx,
    cardSx,
    sidebarResizeLineSx,
  } = getStyles({
    forceCompactMode,
    isDragging,
  });

  const createNewFolder = debounce(
    () =>
      dispatch(
        createFolderEffect({
          title: getNextFolderTitle(songsFolders),
        })
      ),
    700
  );

  const [listId, infiniteScrollProps] = useSongFoldersInfiniteLoader({
    dataLength: songsFolders.length,
  });

  return (
    <Flex ref={panelRef} sx={rootSx}>
      <Flex sx={wrapperSx}>
        <VStack sx={contentSx} height={height} spacing={1}>
          <Card sx={cardSx} px={2} data-testid="navigation-menu">
            {mainMenu.map(({ to, label, icon, activeIcon }) => (
              <SiderBarLink
                key={to}
                to={to}
                label={label}
                icon={icon}
                activeIcon={activeIcon}
                isCompact={isCompact}
              />
            ))}
          </Card>
          <Card
            sx={cardSx}
            flex={1}
            overflow="hidden"
            data-testid="song-folders"
          >
            <Flex
              gap={1}
              px={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Flex
                mx={isCompact ? 'auto' : 2}
                mb={2}
                gap={0.5}
                alignItems="center"
              >
                <IconButton
                  variant="text"
                  minW={4}
                  w={4}
                  h={4}
                  color="M.60"
                  isDisabled={forceCompactMode}
                  mx={isCompact ? 1 : 0}
                  _hover={{ color: 'textBase' }}
                  onClick={() => {
                    if (toggleMinMax() === 'min') {
                      resize({
                        width: getMaxWidth(SIDEBAR_KEY),
                        forceTransition: true,
                      });
                    } else {
                      resize({
                        width: getMinWidth(SIDEBAR_KEY),
                        forceTransition: true,
                      });
                    }
                  }}
                  size="xs"
                  aria-label="collapse-menu"
                >
                  <MenuIcon
                    width={16}
                    height={16}
                    style={{
                      transition: 'transform 500ms',
                      ...(isCompact ? { transform: 'rotate(90deg)' } : {}),
                    }}
                  />
                </IconButton>
                {!isCompact && (
                  <Text
                    flex={1}
                    ml={2}
                    whiteSpace="nowrap"
                    color="textSubdued"
                    fontWeight="medium"
                    visibility={showFullHeader ? 'visible' : 'hidden'}
                  >
                    FOLDERS
                  </Text>
                )}
              </Flex>

              {!isCompact && (
                <IconButton
                  variant="ghost"
                  color="M.60"
                  _hover={{ color: 'textBase' }}
                  onClick={createNewFolder}
                  size="xs"
                  h={4}
                  minW={4}
                  w={4}
                  aria-label="collapse-menu"
                >
                  <AddIcon width={16} height={16} />
                </IconButton>
              )}
            </Flex>
            <Box overflowY="auto" id={listId} px={isCompact ? 0 : 2}>
              <ContextMenu contextMenuProps={contextMenuProps} />
              <InfiniteScroll {...infiniteScrollProps}>
                <DndProvider
                  backend={TouchBackend}
                  options={{ enableMouseEvents: true }}
                >
                  {songFolders.map((songFolder) => (
                    <SongFolder
                      key={songFolder.guid}
                      {...songFolder}
                      isCompact={isCompact}
                      mb="3px"
                    />
                  ))}
                </DndProvider>
              </InfiniteScroll>
            </Box>
          </Card>
        </VStack>
        <Box
          onMouseDown={handleMouseDown}
          cursor={forceCompactMode ? 'default' : cursor}
          sx={sidebarResizeLineSx}
        >
          <Box />
        </Box>
      </Flex>
      <SongFolderRenameDialog
        songFolder={renameSongFolder}
        open={!!renameSongFolder}
        onClose={() => setRenameSongFolder(null)}
      />
      <SongFolderRemoveDialog
        songFolder={removeSongFolder}
        open={!!removeSongFolder}
        onClose={() => setRemoveSongFolder(null)}
      />
    </Flex>
  );
};
